import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/items/{orderItemId}';
const HTTP_METHOD = 'delete';

// Define domain errors with 422 support
export const DeleteProductVariantError = createBackendApiError({
  domain: 'Order',
  endpoint: 'DeleteProductVariant',
  path: ENDPOINT,
  identifierField: 'orderToken',
} as const);

// Export typed error union
export type DeleteProductVariantError = BackendApiErrorUnion<typeof DeleteProductVariantError>;

const deleteOrderItemFetch = (tokenValue: string, orderItemId: number) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json')
      .fetch({
        params: { tokenValue, orderItemId: String(orderItemId) },
      });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'order.token': tokenValue,
        'orderItem.id': String(orderItemId),
      },
    })
  );

export const deleteProductVariantFromOrder = (tokenValue: string, orderItemId: number) =>
  deleteOrderItemFetch(tokenValue, orderItemId).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': Effect.succeed,
        404: (response) =>
          Effect.fail(
            new DeleteProductVariantError.NotFoundError({ orderToken: tokenValue, response })
          ),
        422: DeleteProductVariantError.createViolationError(tokenValue),
        orElse: (response) =>
          Effect.fail(
            new DeleteProductVariantError.ApiRequestError({ orderToken: tokenValue, response })
          ),
      })
    ),
    Effect.retry(orderConflictRetryPolicy),
    Effect.tap(() =>
      logApiOperation(
        ApiModule.OrderItemDelete,
        'Debug',
        `Successfully deleted order item ${String(orderItemId)} from order ${tokenValue}`
      )
    ),
    Effect.withSpan('api.orderItem.delete', {
      kind: 'internal',
      attributes: {
        'order.token': tokenValue,
        'orderItem.id': String(orderItemId),
      },
    })
  );
